import { ChangeDetectorRef, Component, forwardRef, inject, Input, OnChanges, OnInit, Provider, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormControl, NG_VALUE_ACCESSOR, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { isNil } from 'lodash';
import { FormsUtil, GuidEmpty, InputComponent, ModalService, TableCellDirective, TableHeader } from 'mbs-ui-kit';
import { ApplicationStateFacade } from 'mbs-ui-shared@facades/application.facade';
import { UiStorageKey } from 'mbs-ui-shared@models';
import { DefaultSelectData } from 'mbs-ui-shared@models/SelectData';
import { winFileNameValidator } from 'mbs-ui-shared@utils/validators';
import { take } from 'rxjs/operators';
import { StepsHelpers } from 'wizards/helpers/steps-helpers';
import { BackupToRestoreStepValue } from 'wizards/models/backup-to-restore-models';
import { DiskInfoCommunication } from 'wizards/models/base/base-models/plan-disk-info-model';
import { VolumeInfoCommunication } from 'wizards/models/base/base-models/plan-volume-info-model';
import { DestinationStepValue } from 'wizards/models/destination-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';

import {
  AlertComponent,
  CheckboxComponent,
  CheckboxDescription,
  FormGroupComponent,
  NumberComponent,
  RadioComponent,
  RadioDescription,
  SelectComponent,
  TableGridComponent
} from 'mbs-ui-kit';

import { DestinationFolderInputComponent } from 'mbs-ui-shared@components/destination-folder-input/destination-folder-input.component';

import { CommonModule } from '@angular/common';

import { ReactiveFormsModule } from '@angular/forms';

import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { BaseForDestinationStep } from 'wizards/steps/only-restore-steps/base/base-for-destination-step';

type PartitionItem = { id: string; label: string; restoreAs: string };

const DestinationStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => DestinationStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-destination-step',
  templateUrl: './destination-step.component.html',
  providers: [DestinationStepValueAccessor],
  imports: [
    AlertComponent,
    CheckboxComponent,
    CheckboxDescription,
    CommonModule,
    DestinationFolderInputComponent,
    FormGroupComponent,
    I18NextPipe,
    NgbTooltipModule,
    NumberComponent,
    RadioComponent,
    RadioDescription,
    ReactiveFormsModule,
    SelectComponent,
    TableGridComponent,
    InputComponent,
    TableCellDirective
  ]
})
export class DestinationStepComponent extends BaseForDestinationStep<DestinationStepValue> implements OnInit, OnChanges {
  private cdr = inject(ChangeDetectorRef);
  mainService: RemoteManagementWizardsService;
  modalService = inject(ModalService);
  i18nPipe = inject(I18NextPipe);
  private applicationStateFacade = inject(ApplicationStateFacade);

  @Input() diskInfo: DiskInfoCommunication[] = [];
  @Input() isCurrentComputer: boolean;
  @Input() partitions: DefaultSelectData[] = [];
  @Input() restorePartitions: any[] = [];
  @Input() backupToRestoreStep: BackupToRestoreStepValue;

  public readonly elementsSelector = {
    name: {
      virtualImageName: 'destination-step-virtual-image-name',
      notRestorePlanBlock: 'notRestorePlanBlock',
      isVirtualBlock: 'isVirtualBlock',
      isNotVirtualBlock: 'isNotVirtualBlock',
      restorePlanBlock: 'restorePlanBlock',
      saveLocationAsDefault: 'saveLocationAsDefault',
      notUnixOrNBFBlock: 'notUnixOrNBFBlock',
      notUnixBlock: 'notUnixBlock',
      isAllRequired: 'isAllRequired',
      isNotUniq: 'isNotUniq',
      isBoot: 'isBoot',
      diskSpace: 'diskSpace',
      notCurrentNotUnix: 'notCurrentNotUnix'
    }
  };

  public minimumSize: number;
  public minimumSizeGB: number | string;
  public maximumSizeGB: number | string;

  public partitionsForSelectsData: { [key: string]: Array<DefaultSelectData & { isSelected?: boolean }> } = {};
  public diskItems: DefaultSelectData[] = [];
  public selectedDisk = '';
  public myPartitions: PartitionItem[] = [];

  public headers: TableHeader[] = [
    { name: 'Selected partition', gridColSize: '2fr' },
    { name: 'Volume', overflow: true, gridColSize: '3fr', class: 'w-100' }
  ];

  public numberIsFocused = false;

  public destinationAsDefault = '';

  private cashStepData = null;

  private targetPartitions: DefaultSelectData[] = [];

  get minimumSizeNumber(): number {
    return typeof this.minimumSizeGB === 'number' ? this.minimumSizeGB : parseFloat(this.minimumSizeGB);
  }

  get maximumSizeNumber(): number {
    return typeof this.maximumSizeGB === 'number' ? this.maximumSizeGB : parseFloat(this.maximumSizeGB);
  }

  get destinationFolderControl(): FormControl<string> {
    return this.stepForm.get('destinationFolder') as FormControl<string>;
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();

    this.mainService = mainService;
  }

  ngOnInit(): void {
    this.initForm();
    this.updatesAfterInitForm();
  }

  initForm(): void {
    this.createStepForm();
    this.initFormEvents();
  }

  /**
   * Separated function for unit tests
   * @private
   */
  private createStepForm(): void {
    const virtualImageNameValidators = [Validators.required];

    if (!this.isUnix) {
      virtualImageNameValidators.push(winFileNameValidator);
    }

    this.stepForm = new UntypedFormGroup({
      destinationFolder: new FormControl(''),
      virtualImageName: new FormControl('', virtualImageNameValidators),
      capacity: new FormControl('0.02', Validators.required),
      minCapacity: new FormControl(0.02),
      toOriginalLocation: new FormControl(false),
      saveLocationAsDefault: new FormControl(false),
      restoreDeletedFiles: new FormControl(false),
      overwriteExistingFiles: new FormControl(false),
      restoreOnlyNewFiles: new FormControl(false),
      restoreNTFSPermissions: new FormControl(false),
      isPhysical: new FormControl(true),
      selectedDisk: new FormControl('', [
        Validators.required,
        this.selectedDiskIsBootValidate.bind(this),
        this.selectedDiskSpaceValidate.bind(this)
      ]),
      selectedSpecificPartitions: new FormArray([], this.selectedSpecificPartitionsValidator.bind(this))
    });
  }

  selectedSpecificPartitionsValidator(control: FormArray): ValidationErrors | null {
    if (!control?.value?.length) return null;

    if (control.controls.some((c) => c.errors && c.errors.isAllRequired)) return { isAllRequired: true };
    if (control.controls.some((c) => c.errors && c.errors.isNotUniq)) return { isNotUniq: true };
    if (control.controls.some((c) => c.errors && c.errors.diskSpace)) return { diskSpace: true };
    if (control.controls.some((c) => c.errors && c.errors.isBoot)) return { isBoot: true };

    return null;
  }

  specificPartitionsValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return { isAllRequired: true };

    const dataFromString = control.value.split('--');
    const partitions = this.mainService.targetComputerData && this.isRestoreIbb ? this.targetPartitions : this.partitions;

    if (!dataFromString[0] || !partitions.some((p) => p.value === dataFromString[0])) {
      return { isAllRequired: true };
    }

    const parts = this.stepForm?.get('selectedSpecificPartitions') || null;

    if (!parts || parts?.errors?.isAllRequired) return null;

    const notUniq = parts.value.some((part) => {
      const arrayFromPart = part.split('--');
      return dataFromString[2] !== arrayFromPart[2] && dataFromString[0] === arrayFromPart[0];
    });

    if (notUniq) return { isNotUniq: true };

    const ids = dataFromString[0].split('__');
    const foundDisk: any = this.mainService.Plan$?.value?.DiskVolumes?.find((v) => v.Identity === ids[0] && v.DiskId === ids[1]);

    if (!foundDisk) return null;

    if (foundDisk.Length < +dataFromString[1]) return { diskSpace: true };

    const isBoot = this.diskInfo.some((disk: DiskInfoCommunication) =>
      disk.Volumes.some((v: VolumeInfoCommunication) => v.Identity === ids[0] && v.DiskId === ids[1] && v.IsBoot)
    );

    if (foundDisk.IsBoot || isBoot) return { isBoot: true };

    return null;
  }

  selectedDiskIsBootValidate(control: AbstractControl): ValidationErrors | null {
    if (control.value && control.value !== GuidEmpty) {
      const found = this.diskInfo.find((disk) => disk.DiskId === control.value);

      return found && found.Volumes && found.Volumes.some((v) => v.IsBoot) ? { isBoot: true } : null;
    }

    return null;
  }

  selectedDiskSpaceValidate(control: AbstractControl): ValidationErrors | null {
    if (control.value && control.value !== GuidEmpty) {
      const found = this.diskInfo.find((disk) => disk.DiskId === control.value);
      const max = this.restorePartitions.reduce((acc, next) => {
        acc += next.length;
        return acc;
      }, 0);

      return found && found.Capacity < max ? { diskSpace: true } : null;
    }

    return null;
  }

  updateRestoreDeletedFilesFromChanges(): void {
    if (!this.isUnix || this.isNetBuild || this.isNBF) {
      this.stepForm.get('restoreDeletedFiles').enable();
      return void this.stepForm.get('restoreDeletedFiles').setValue(this.isCreate || this.value.restoreDeletedFiles);
    }

    this.stepForm.get('restoreDeletedFiles').disable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.stepForm) {
      if (changes.backupToRestoreStep) this.updateAfterChangeBackupToRestoreStep();
      if (changes.isVirtual) queueMicrotask(() => this.updateFormFromIsVirtual(changes.isVirtual.currentValue));

      this.updateRestoreNTFSPermissions();

      if (changes.isCurrentComputer) this.updateFileRestoreFromIsCurrentComputer();
    }

    if (changes.diskInfo && changes.diskInfo.currentValue && changes.diskInfo.currentValue.length) {
      if (!this.mainService.targetComputerData || !this.isRestoreIbb) this.setDiskItems(changes.diskInfo.currentValue);
      else {
        const diskInfo = this.mainService.loadedDisksInfoForIbb();
        this.targetPartitions = diskInfo.volumes.map((disk) => ({
          value: `${disk.Identity}__${disk.DiskId}__${disk.WindowsVolumeIdentity}`,
          label: `${disk.LengthString} (${disk.UsedSpaceString}) ${disk.MountPointsInfo || disk.Label}`
        }));

        if (diskInfo.disks?.length) this.setDiskItems(diskInfo.disks);
      }
    }

    if (changes.restorePartitions?.currentValue?.length) {
      if (this.stepForm) return this.setPartitions(changes.restorePartitions.currentValue);
      if (this.isEdit && this.isVirtual) return queueMicrotask(() => this.setPartitions(changes.restorePartitions.currentValue));

      if (this.isRestoreIbb && this.partitions?.length && this.restorePartitions?.length) {
        queueMicrotask(() => this.setPartitions(changes.restorePartitions.currentValue));
      }
    }
  }

  updateFileRestoreFromIsCurrentComputer(): void {
    if (!this.isRestorePlan) return;
    if (this.isCurrentComputer) return this.stepForm.get('toOriginalLocation').enable();

    this.stepForm.get('toOriginalLocation').setValue(false);
    this.stepForm.get('toOriginalLocation').disable();
    this.toOriginalLocationChangeHandler(false);
  }

  setMinMaxSizes(restorePartitions): void {
    if (!this.isRestoreIbb || !this.isVirtual) return;

    this.minimumSize = 0;
    this.maximumSizeGB = 0;

    restorePartitions.forEach((part) => {
      if (part.targetSize) this.minimumSize += +part.targetSize;
      if (part.uiDiskLimit && part.uiDiskLimit > this.maximumSizeGB) this.maximumSizeGB = +part.uiDiskLimit.toFixed(2);
    });

    this.minimumSizeGB = !this.minimumSize ? 0.02 : +Math.ceil(this.minimumSize / 1024).toFixed(2);
    this.stepForm.get('minCapacity').setValue(this.minimumSizeGB);
    this.stepForm.get('capacity').setValue(this.minimumSizeGB || 0.02);
  }

  setDiskItems(items: any[]): void {
    this.diskItems = items.map((disk) => ({
      value: disk.DiskId,
      label: `HDD: ${disk.DiskNumber || ''} ${disk.DriveType} ${
        disk.Capacity ? (disk.Capacity / 1024 / 1024 / 1024).toFixed(1) + ' GB' : ''
      } ${disk.Model || ''}`
    }));

    if (this.isCreate && this.diskItems?.length) {
      this.selectedDisk = this.diskItems[0].value;
      setTimeout(() => this.stepForm.get('selectedDisk').setValue(this.diskItems[0].value));
    }
  }

  updatePartitionItem(disk, selectedParts, partitions = null, idx = 0): PartitionItem {
    const parent = this.diskInfo?.length ? this.diskInfo.find((d) => d.DiskId === disk.diskId) : null;
    const fromForm = selectedParts
      ? selectedParts.find((p) => {
          const id = p.split('--');
          const idNext = id[2] ? id[2].split('__') : [];
          return idNext[0] == disk.identity && idNext[1] == disk.diskId;
        })
      : null;
    const lengthStr = disk.lengthString || StepsHelpers.getDiskSizeFromMB(disk.length / 1024 / 1024) || '-';

    // volumeTarget;
    this.partitionsForSelectsData[disk.identityForUiField] = (partitions ? partitions : this.partitions).map((p) => {
      return {
        label: p.label,
        value: `${p.value}--${disk.length}--${disk.identityForUiField}`
      };
    });

    const firstSlice = fromForm
      ? fromForm.split('--')[0] || disk.identityForUiField || ''
      : partitions
        ? partitions?.[idx]?.value || ''
        : parent
          ? disk.identityForUiField
          : '';

    return {
      id: disk.identityForUiField,
      label: `${lengthStr} ${
        parent?.Model || disk.mountPointsInfo || disk.label || (disk.typeAsString ? 'Type: ' + disk.typeAsString : '')
      }`,
      restoreAs: `${firstSlice}--${disk.length}--${disk.identityForUiField}`
    };
  }

  setPartitions(items: any[]): void {
    this.setMinMaxSizes(items);
    const myPartitionsForm = this.stepForm ? (this.stepForm.get('selectedSpecificPartitions') as FormArray) : new FormArray([]);
    const selectedParts = this.isEdit && this.stepForm ? myPartitionsForm.value : null;

    if (this.mainService.targetComputerData) {
      const diskInfo = this.mainService.loadedDisksInfoForIbb();
      this.targetPartitions = diskInfo.volumes.map((disk) => ({
        value: `${disk.Identity}__${disk.DiskId}__${disk.WindowsVolumeIdentity}`,
        label: `${disk.LengthString} (${disk.UsedSpaceString}) ${disk.MountPointsInfo || disk.Label}`
      }));

      if (diskInfo.disks?.length) this.setDiskItems(diskInfo.disks);

      this.myPartitions = items.map((disk, idx) => this.updatePartitionItem(disk, selectedParts, this.targetPartitions, idx));
    } else {
      this.myPartitions = items.map((disk) => this.updatePartitionItem(disk, selectedParts));
    }

    myPartitionsForm.clear();
    this.myPartitions.forEach((part) => {
      myPartitionsForm.push(new FormControl(part.restoreAs, this.specificPartitionsValidator.bind(this)));
    });

    this.updateFormFromIsVirtual(this.isVirtual);
    this.selectedSpecificPartitionsChangeHandler();
    this.cdr.detectChanges();
  }

  updateForm(value: DestinationStepValue): void {
    this.setFormControls(value);

    this.updateDestinationFolderData(value);

    if (
      this.isEdit &&
      !this.isVirtual &&
      this.isRestoreIbb &&
      !this.isCurrentComputer &&
      this.restorePartitions &&
      this.restorePartitions.length
    ) {
      this.setPartitions(this.restorePartitions);
    }

    this.selectedDisk = value.selectedDisk;
    if (this.isRestoreIbb) {
      this.updateFormFromIsVirtual(this.isVirtual);
      this.isPhysicalChangeHandler(value.isPhysical);
      if (this.isVirtual) {
        this.stepForm.get('selectedDisk').disable();
        this.stepForm.get('selectedSpecificPartitions').disable();
      }
    }

    !isNil(value.toOriginalLocation) && this.toOriginalLocationChangeHandler(value.toOriginalLocation);
    !isNil(value.overwriteExistingFiles) && this.overwriteChangeHandler(value.overwriteExistingFiles);

    if (this.isRestorePlan) {
      this.stepForm.get('capacity').disable();
      this.stepForm.get('isPhysical').disable();
      this.stepForm.get('selectedDisk').disable();
      this.stepForm.get('selectedSpecificPartitions').disable();
      this.stepForm.get('virtualImageName').disable();

      if (this.isCreate) this.updateFileRestoreFromIsCurrentComputer();
    }
  }

  setFormControls(value: DestinationStepValue): void {
    Object.keys(value).forEach((key) => {
      if (key === 'selectedSpecificPartitions') this.updateSelectedPartitions(value.selectedSpecificPartitions);
      else if (key !== 'valid') this.stepForm.get(key).reset(value[key]);
    });
  }

  updateSelectedPartitions(partitions: string[]): void {
    if (!partitions?.length) return;

    const myPartitionsForm = this.stepForm ? (this.stepForm.get('selectedSpecificPartitions') as FormArray) : new FormArray([]);

    if (!myPartitionsForm?.value?.length && !this.myPartitions?.length) {
      return void partitions?.forEach((part) => myPartitionsForm.push(new FormControl(part, this.specificPartitionsValidator.bind(this))));
    }

    partitions.forEach((part, idx) => myPartitionsForm.controls[idx].setValue(part));
  }

  forceValid(): void {
    this.stepForm.markAsTouched();
    FormsUtil.triggerValidation(this.stepForm);
    this.updateFormValidatorsState();
    this.resetValidStateForValidFields();
  }

  updateFormValidatorsState(): void {
    if (!this.isVirtual && !this.stepForm.get('isPhysical').value) {
      (this.stepForm.get('selectedSpecificPartitions') as FormArray).controls.forEach((control) => {
        control.markAsDirty();
        control.markAsTouched();
        control.updateValueAndValidity();
      });
    }
  }

  updateFormFromIsVirtual(isVirtual: boolean): void {
    this.toggleFormControls(['destinationFolder', 'virtualImageName', 'overwriteExistingFiles'], isVirtual);

    if (isVirtual) {
      this.toggleFormControls(['isPhysical', 'selectedDisk', 'selectedSpecificPartitions'], false);

      if (!this.isRestoreIbb) return;

      const currentCapacity = this.stepForm.get('capacity').value;

      if (!this.stepForm.get('capacity').value || (!this.isEdit && currentCapacity < +this.minimumSizeGB)) {
        this.stepForm.get('minCapacity').reset(this.minimumSizeGB);
        this.stepForm.get('capacity').reset(this.minimumSizeGB || 0.02);
      }

      return this.stepForm.get('capacity').enable();
    }

    this.stepForm.get('capacity').disable();
    this.stepForm.get('isPhysical').enable();

    if (this.isRestoreIbb) this.isPhysicalChangeHandler(this.stepForm.get('isPhysical').value);
  }

  capacityBlurHandler(): void {
    this.numberIsFocused = false;
    this.updateCapacityInput(+this.stepForm.get('capacity').value, true);
  }

  capacityFocusHandler(): void {
    this.numberIsFocused = true;
  }

  capacityInputHandler(capacity: number): void {
    if (!this.numberIsFocused) this.updateCapacityInput(capacity);
  }

  updateCapacityInput(capacity: number, fromBlur = false): void {
    if (fromBlur && capacity < +this.minimumSizeGB) {
      return this.stepForm.get('capacity').setValue((+this.minimumSizeGB).toFixed(2));
    }

    if (fromBlur && capacity > +this.maximumSizeGB) {
      return this.stepForm.get('capacity').setValue((+this.maximumSizeGB).toFixed(2));
    }

    const capacityDec = capacity ? capacity.toString().split('.') : [];

    if (capacityDec[1]?.length > 2) {
      this.stepForm.get('capacity').setValue(Math.ceil(capacity * 100) / 100);
    }
  }

  isPhysicalChangeHandler(event: boolean): void {
    if (event) {
      this.stepForm.get('selectedDisk').enable();
      this.stepForm.get('selectedSpecificPartitions').disable();
      return;
    }

    this.stepForm.get('selectedDisk').disable();

    if (!this.stepForm.get('selectedDisk')?.getRawValue()) {
      this.stepForm.get('selectedDisk').reset(this.cashStepData?.selectedDisk || this.diskItems?.[0]?.value || '');
    }

    this.stepForm.get('selectedSpecificPartitions').enable();
  }

  getPartitionControl(index: number): FormControl {
    return (this.stepForm.get('selectedSpecificPartitions') as FormArray).at(index) as FormControl;
  }

  overwriteChangeHandler(event: boolean): void {
    if (event) this.stepForm.get('restoreOnlyNewFiles').enable();
    else {
      this.stepForm.get('restoreOnlyNewFiles').setValue(false);
      this.stepForm.get('restoreOnlyNewFiles').disable();
    }
  }

  toOriginalLocationChangeHandler(event: boolean): void {
    if (isNil(event) || (event as unknown as { target?: HTMLElement }).target) return;

    this.toggleFormControls(['destinationFolder'], !event && (!this.isRestoreIbb || this.isVirtual));
  }

  selectedSpecificPartitionsChangeHandler(): void {
    const formArray = this.stepForm.get('selectedSpecificPartitions') as FormArray;
    const keys = Object.keys(this.partitionsForSelectsData);
    const partLength = this.partitionsForSelectsData[keys[0]].length;
    keys.forEach((key) => this.partitionsForSelectsData[key].forEach((el) => (el.isSelected = false)));
    formArray.value.forEach((part: string) => {
      const arrayFromPart = part.split('--');
      keys.forEach((key) => {
        if (key !== arrayFromPart[2]) {
          for (let i = 0; i < partLength; i++) {
            const newId = this.partitionsForSelectsData[key][i].value.split('--')[0];
            if (newId === arrayFromPart[0]) {
              this.partitionsForSelectsData[key][i].isSelected = true;
              break;
            }
          }
        }
        this.partitionsForSelectsData[key] = Array.from(this.partitionsForSelectsData[key]);
      });
    });
    this.partitionsForSelectsData = Object.assign({}, this.partitionsForSelectsData);
    this.updateFormValidatorsState();
  }

  private updatesAfterInitForm(): void {
    if (this.isUnix && !this.isNetBuild) {
      this.stepForm.get('saveLocationAsDefault').disable();

      if (!this.isNBF) this.stepForm.get('restoreDeletedFiles').disable();
    }

    this.setValues();
    this.destinationFolderParams = this.getParamsForDestinationFolder();

    if ((this.isUnix && !this.isNetBuild) || !this.isRestorePlan) return;

    this.applicationStateFacade
      .getUiStorage()
      .pipe(take(1), untilDestroyed(this))
      .subscribe({
        next: (storage) => {
          const storageValue = storage?.[UiStorageKey.destinationAsDefault];

          if (!storageValue) return;

          const defaultPath = JSON.parse(storageValue)[this.mainService.hid] || '';

          if (!defaultPath) return;

          this.destinationAsDefault = defaultPath;

          if (!this.isCreate) return;

          this.stepForm.get('saveLocationAsDefault').setValue(true);
          this.stepForm.get('destinationFolder').setValue(defaultPath);
        }
      });
  }

  private updateDestinationFolderData(value: DestinationStepValue): void {
    if (value.destinationFolder || this.isEdit || (this.isUnix && !this.isNetBuild) || !this.isRestorePlan) return;
    if (!this.destinationAsDefault) return;

    this.destinationFolderControl.setValue(this.destinationAsDefault);
  }

  private updateRestoreNTFSPermissions(): void {
    if (this.isUnix && !this.isNetBuild) {
      return queueMicrotask(() => this.stepForm.get('restoreNTFSPermissions').disable());
    }

    if (this.isRestorePlan && this.isEdit) {
      queueMicrotask(() => this.stepForm.get('restoreNTFSPermissions').enable());
    } else this.stepForm.get('restoreNTFSPermissions').enable();
  }

  private updateAfterChangeBackupToRestoreStep(): void {
    if ((this.isUnix && !this.isNetBuild) || (this.isRestorePlan && this.isEdit)) {
      return queueMicrotask(() => this.updateRestoreDeletedFilesFromChanges());
    }

    this.updateRestoreDeletedFilesFromChanges();
  }
}
